import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {CAMPAIGN_APPLICATION_KEYS} from "@/api/campaignApplicationApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import LikeHeartEmpty from "@/assets/colorable/like_heart_icon.svg?react";
import LikeHeartFilled from "@/assets/light_gradient/heart_filled.svg?react";
import {useSelectedCompanyId} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import {queryClient} from "@/queryClient";
import {useCampaignGalleryStore} from "@/store/campaignGallery";
import useCampaignManagementStore from "@/store/campaignManagement";
import {useCarouselStore} from "@/store/carousel";
import {useFavouriteContentStore} from "@/store/favouriteContent";
import type {TInstagramMedia} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import {useMemo, useState, type FC} from "react";
import {useLocation} from "react-router";

const ContentLikeButton: FC<{
  content: {instagram_media: TInstagramMedia | null};
  className?: string;
}> = ({content, className}) => {
  const location = useLocation();
  const isOnDashboard = location.pathname.includes("/dashboard/");
  const campaign =
    content?.instagram_media?.campaign_application?.campaign ?? useCampaignManagementStore(state => state.campaign);
  const activeStoreType = useFavouriteContentStore(state => state.active);
  const campaignType = useMemo(
    () => (isOnDashboard ? activeStoreType : campaign.ugc ? "ugc" : campaign.tiktok ? "tiktok" : "instagram"),
    [isOnDashboard, activeStoreType, campaign],
  );
  const dateFilter = useCampaignManagementStore(state => state.reportingDateFilter);
  const gallerySearchParams = useCampaignGalleryStore(state => state.searchParams);
  const toggleLikedStatus = useCarouselStore(state => state.toggleLikedStatus);
  const [isLiked, setLiked] = useState(content.instagram_media?.liked);

  const {mutate: toggledLikedStatus, isPending} = useMutation({
    mutationKey: CAMPAIGN_APPLICATION_KEYS.mApplicationLikedMedia,
    mutationFn: () =>
      client.post(CAMPAIGN_APPLICATION_KEYS.mApplicationLikedMedia[0], {
        media_id: content.instagram_media?.id,
        media_type: content.instagram_media?.media_product_type === "SOURCE" ? "SOURCE" : campaignType.toUpperCase(),
      }),
    onSuccess: () => {
      setLiked(!isLiked);

      const companyId = useSelectedCompanyId();
      const favouriteContentSearchParams = useFavouriteContentStore
        .getState()
        .selectActiveStore()
        .getState().searchParams;

      [
        CAMPAIGN_KEYS.qCampaignReportingApplications({campaignId: campaign.id!, dateFilter}),
        CAMPAIGN_KEYS.qCampaignFeaturedContent(campaign.id!, dateFilter),
        CAMPAIGN_KEYS.qCampaignGallery(campaign.id!, gallerySearchParams),
        CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id!),
        COMPANY_KEYS.qCompanyFavouriteContent(companyId!, favouriteContentSearchParams!),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));

      toggleLikedStatus(content.instagram_media?.id!);
    },
  });

  return (
    <Can I="create" a="FavoriteContentLike">
      <button
        onClick={e => {
          e.stopPropagation();
          toggledLikedStatus();
        }}
        className={clsxm(
          "border-neutral-white-dark hover:bg-neutral-white-dark absolute top-0 right-0 z-50 inline-block -translate-x-2 translate-y-2 items-center justify-center rounded-full border bg-white p-2 shadow-xs",
          className,
        )}
        disabled={isPending}>
        {isLiked ? (
          <LikeHeartFilled className="size-4.5 pt-0.5" />
        ) : (
          <LikeHeartEmpty className="text-primary-dark-solid size-4.5 pt-0.5" />
        )}
      </button>
    </Can>
  );
};

export default ContentLikeButton;
